// Route-transition fallback skeletons are statically imported into the entry
// graph, so they must stay lean (Skeleton primitive only, no data/api deps).
import { HomePageSkeleton } from '@/client/components/page-skeletons/home-skeleton';
import { PageSkeleton } from '@/client/components/page-skeletons/page-skeleton';
import {
  ACTIVITY_DISTRIBUTOR_ADDRESS,
  ACTIVITY_ENROLLMENT_ENABLED,
  IS_DEBUG,
  NOTIFICATIONS_ENABLED,
} from '@/client/confs/app';
import { PAGE_LOADERS } from '@/client/confs/page-loaders';
import { EventPageSkeleton } from '@/client/pages/event/event-skeleton';
import TheHomePage from '@/client/pages/home';
import { lazy, type FC, type PropsWithChildren, type ReactNode } from 'react';

const TheErrorPage = lazy(PAGE_LOADERS.error);
const TheEventPage = lazy(PAGE_LOADERS.event);
const TheHowItWorksPage = lazy(PAGE_LOADERS['how-it-works']);
const TheLeaderboard = lazy(PAGE_LOADERS.leaderboard);
const TheMarketPage = lazy(PAGE_LOADERS.market);
const ThePDFPage = lazy(PAGE_LOADERS.pdf);
const TheUserPage = lazy(PAGE_LOADERS.user);
const TheUserAuthRedirect = lazy(PAGE_LOADERS['user-auth-redirect']);
const TheInviteEarnPage = lazy(PAGE_LOADERS['user-invite-earn']);
const TheNotificationPage = lazy(PAGE_LOADERS['user-notification']);
const TheUserSettingsPage = lazy(PAGE_LOADERS['user-settings']);
const PreviewHome = lazy(PAGE_LOADERS.preview);

export type RouteItem = {
  name: string;
  path: string;
  component: FC | JSX.Element;
  fallback?: ReactNode;
  layout?: FC<PropsWithChildren>;
  children?: RouteItem[];
};

// Minimal layout without any providers
// eslint-disable-next-line react-refresh/only-export-components -- route table module, never hot-swapped alone
const MinimalLayout: FC<PropsWithChildren> = ({ children }) => <>{children}</>;

export const routes: RouteItem[] = [
  // 5-minute rounds have no route of their own: each round is an event, and /event/:id opens the
  // 5-minute page for an id listed in the short-market feed (pages/event/short-round-gate.tsx).
  // Claims outlive enrollment (A31): a configured distributor keeps /rewards up.
  ...(ACTIVITY_ENROLLMENT_ENABLED || ACTIVITY_DISTRIBUTOR_ADDRESS
    ? [
        {
          path: '/rewards',
          name: 'activity-rewards',
          component: lazy(PAGE_LOADERS['activity-rewards-live']),
          fallback: <PageSkeleton />,
        },
      ]
    : []),
  // Preview route (Figma mock data, no backend). DEBUG-only: it renders mock
  // balances and parent-brand demo content, so it must never register in a
  // production build (IS_DEBUG is false for the dpin-bsc build).
  ...(IS_DEBUG
    ? [
        {
          path: '/preview',
          name: 'preview',
          component: PreviewHome,
          layout: MinimalLayout,
        } as RouteItem,
      ]
    : []),
  // Original routes
  {
    path: '/',
    name: 'home',
    component: TheHomePage,
    fallback: <HomePageSkeleton />,
  },
  {
    path: '/event/:id',
    name: 'event',
    component: TheEventPage,
    fallback: <EventPageSkeleton />,
  },
  {
    path: '/market/:id',
    name: 'market',
    component: TheMarketPage,
    fallback: <EventPageSkeleton />,
  },
  // Shortcut redirects — auth-guarded, redirect to /user/{uid}/...
  // Notifications off (BON-249): no notification route is registered, so the
  // notification paths reach the not-found page like the other hidden features.
  ...(NOTIFICATIONS_ENABLED
    ? [
        {
          path: '/notification',
          name: 'notification-redirect',
          component: <TheUserAuthRedirect suffix="/notification" />,
        },
      ]
    : []),
  {
    path: '/profile',
    name: 'profile-redirect',
    component: TheUserAuthRedirect,
  },
  // Auth-guarded redirects for /user and /user/notification without :id
  {
    path: '/user',
    name: 'user-auth-redirect',
    component: TheUserAuthRedirect,
  },
  // With notifications off this path still needs an entry, because /user/:id
  // below would match it as a uid; the entry renders the not-found page.
  NOTIFICATIONS_ENABLED
    ? {
        path: '/user/notification',
        name: 'user-notification-auth-redirect',
        component: <TheUserAuthRedirect suffix="/notification" />,
      }
    : {
        path: '/user/notification',
        name: 'user-notification-not-found',
        component: TheErrorPage,
      },
  {
    path: '/user/:id',
    name: 'user',
    component: TheUserPage,
    fallback: <PageSkeleton />,
    children: [
      {
        path: '/settings',
        name: 'user-settings',
        component: TheUserSettingsPage,
      },
      ...(NOTIFICATIONS_ENABLED
        ? [
            {
              path: '/notification',
              name: 'user-notification',
              component: TheNotificationPage,
            },
          ]
        : []),
      {
        path: '/invite-earn',
        name: 'user-invite-earn',
        component: TheInviteEarnPage,
      },
    ],
  },
  {
    path: '/leaderboard',
    name: 'leaderboard',
    component: TheLeaderboard,
    fallback: <PageSkeleton />,
  },
  {
    path: '/how-it-works',
    name: 'how-it-works',
    component: TheHowItWorksPage,
  },
  {
    path: '/pdf',
    name: 'pdf',
    component: ThePDFPage,
  },
  {
    path: '*',
    name: 'error',
    component: TheErrorPage,
  },
];
